/**
 * dataFetch — routes GET requests to IndexedDB when offline,
 * falls back to the live API when online.
 *
 * Usage (in pages):
 *   const data = await dataFetch("getClass", { class_id, session, semester, level });
 */

import * as offlineQuery from "./offlineQuery";
import { isSyncedDataAvailable } from "./syncManager";
import { API_BASE_URL } from "../config/api";

const QUERY_MAP = {
  getSessions: () => offlineQuery.getSessions(),
  getClass: (p) => offlineQuery.getClass(p),
  getStudentResults: (p) => offlineQuery.getStudentResults(p.student_id),
  getProbationList: (p) => offlineQuery.getProbationList(p),
  getCatalogue: () => offlineQuery.getCatalogue(),
  searchStudent: (p) => offlineQuery.searchStudent(p.reg_no),
};

// ── Real connectivity check ───────────────────────────────────────────────────
// navigator.onLine is unreliable — it returns true even on local-only networks.
// We do a lightweight HEAD request to the API to confirm real connectivity.
// Cache the result for 10 seconds to avoid hammering on every page load.

let _lastCheck = 0;
let _lastResult = true;
const CHECK_TTL = 10_000;

export async function isOnline() {
  const now = Date.now();
  if (now - _lastCheck < CHECK_TTL) return _lastResult;
  try {
    const res = await fetch(`${API_BASE_URL}/`, {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(4000),
    });
    // 500 means server is up but broken — still treat as offline for data purposes
    // Only trust 2xx and 3xx as truly online
    _lastResult = res.status >= 200 && res.status < 500;
  } catch {
    _lastResult = false;
  }
  _lastCheck = now;
  return _lastResult;
}

// ── Main fetch router ─────────────────────────────────────────────────────────

export async function dataFetch(queryName, params = {}) {
  const online = await isOnline();

  if (!online) {
    const hasData = await isSyncedDataAvailable();
    if (!hasData) {
      throw new Error(
        "You are offline and no synced data is available. Please sync while online first.",
      );
    }
    const queryFn = QUERY_MAP[queryName];
    if (!queryFn) throw new Error(`No offline query defined for: ${queryName}`);
    return queryFn(params);
  }

  // Online — caller should use authFetch directly.
  // This is a safety net that should not normally be reached.
  throw new Error(
    `dataFetch called while online — use authFetch for: ${queryName}`,
  );
}

export { isSyncedDataAvailable };
